"use client"

import { useId } from "react"
import { useField } from "@payloadcms/ui"
import type { NumberFieldClientComponent } from "payload"

import { Input } from "@/components/ui/input"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Number field. Stores the value as a number
 * (or `null` when the input is cleared) so the form data type matches
 * what the schema expects. Empty string -> `null` not `0` so optional
 * numeric fields don't get coerced.
 */
export const ShadcnNumberField: NumberFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<
    number | null
  >({ path: path ?? fieldName })
  const id = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const placeholder =
    ((field as { admin?: { placeholder?: unknown } })?.admin?.placeholder as
      | string
      | undefined) ?? undefined
  const required = Boolean((field as { required?: boolean }).required)
  const min = (field as { min?: number })?.min
  const max = (field as { max?: number })?.max
  const step = (field as { admin?: { step?: number } })?.admin?.step ?? "any"

  const display =
    value === null || value === undefined || Number.isNaN(value as number)
      ? ""
      : String(value)

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={id}
    >
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={display}
        onChange={(e) => {
          const raw = e.target.value
          if (raw === "") {
            setValue(null)
            return
          }
          const n = Number(raw)
          setValue(Number.isFinite(n) ? n : null)
        }}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step as number | "any"}
        aria-invalid={showError ? true : undefined}
        className="h-9 rounded-xl tabular-nums"
      />
    </FieldShell>
  )
}

export default ShadcnNumberField
